import React, {
  Suspense,
  useCallback,
  useMemo,
  useReducer,
  useState,
  useEffect,
} from "react";
import PropTypes from "prop-types";
import { ArrowDownward, ArrowUpward, FilterList } from "@mui/icons-material";
import {
  Box,
  Checkbox,
  IconButton,
  MenuItem,
  Select,
  TablePagination,
  Typography,
  useTheme,
} from "@mui/material";

import { ButtonV1, Loader, PaginationAction } from "components";
import SellerCard from "./SellerCard";

import { getSellerVmId } from "../makeData";
import { SELLER_SORT_OPTIONS } from "../constants";

const FilterModal = React.lazy(() => import("./FilterModal"));

const filterReducer = (state, action) => {
  if (action.type === "open") return true;
  if (action.type === "close") return false;
  return state;
};

const SellerList = ({
  sellerData = [],
  selectedVmIds = [],
  onToggleSeller,
  onToggleAllSellers,
  onClearSelection,
  onSortChange,
  onApplyFilters,
  sellerFilterData,
  customersData,
  fetchCustomersData,
  buyerLoading,
  sortConfig,
  sellerPagination,
  onPageChange,
  onRowsPerPageChange,
  filterResetKey,
  loading,
}) => {
  const theme = useTheme();
  const [openFilter, dispatchFilter] = useReducer(filterReducer, false);
  const [appliedFilters, setAppliedFilters] = useState(null);

  const vmIds = useMemo(
    () => sellerData.map((seller) => getSellerVmId(seller)).filter(Boolean),
    [sellerData],
  );
  const selectedVmIdsSet = useMemo(
    () => new Set(selectedVmIds),
    [selectedVmIds],
  );

  const allSelected = useMemo(
    () => vmIds.length > 0 && vmIds.every((vmId) => selectedVmIdsSet.has(vmId)),
    [selectedVmIdsSet, vmIds],
  );
  const selectedCount = selectedVmIds.length;
  const totalCount = sellerPagination?.totalItems ?? sellerData.length;
  const requestedPage = Math.max(
    0,
    Number(sellerPagination?.page ?? sellerPagination?.currentPage ?? 1) - 1,
  );
  const rowsPerPage = 10;
  const currentPage = Math.min(
    requestedPage,
    Math.max(0, Math.ceil(totalCount / rowsPerPage) - 1),
  );
  const activeSort = sortConfig?.sortBy || SELLER_SORT_OPTIONS[0].value;

  const handleSortChange = useCallback(
    (event) => {
      onSortChange?.(event.target.value);
    },
    [onSortChange],
  );

  const handleApplyFilters = useCallback(
    (data) => {
      setAppliedFilters(data);
      onApplyFilters?.(data);
    },
    [onApplyFilters],
  );

  const handleToggleAll = useCallback(() => {
    onToggleAllSellers(allSelected ? [] : vmIds);
  }, [allSelected, onToggleAllSellers, vmIds]);

  const handleOpenFilter = useCallback(() => {
    dispatchFilter({ type: "open" });
  }, []);

  const handleCloseFilter = useCallback(() => {
    dispatchFilter({ type: "close" });
  }, []);

  const handleResetFilters = useCallback(() => {
    setAppliedFilters(null);
    onApplyFilters({});
  }, [onApplyFilters]);

  useEffect(() => {
    setAppliedFilters(null);
  }, [filterResetKey]);

  const sellerCards = useMemo(
    () =>
      sellerData.map((seller) => {
        const vmId = getSellerVmId(seller);
        return (
          <SellerCard
            key={vmId}
            seller={seller}
            checked={selectedVmIdsSet.has(vmId)}
            onChange={onToggleSeller}
          />
        );
      }),
    [onToggleSeller, selectedVmIdsSet, sellerData],
  );

  return (
    <Box
      sx={{
        height: "100%",
        border: "1px solid",
        borderColor: theme.palette.divider,
        borderRadius: 0,
        backgroundColor: theme.palette.common.white,
        p: 1.5,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
        <Typography sx={{ fontSize: 14, fontWeight: 700 }}>
          Sellers{" "}
          <Typography
            component="span"
            sx={{ color: theme.palette.grey[1000], fontSize: 14 }}
          >
            ({totalCount})
          </Typography>
        </Typography>

        <Box sx={{ flex: 1 }} />

        <ButtonV1
          title="Clear"
          size="small"
          onClick={onClearSelection}
          style={{
            fontWeight: 500,
          }}
        />
      </Box>

      <Typography
        sx={{
          color: theme.palette.grey[1000],
          fontSize: 14,
          mb: 0.5,
          fontWeight: 700,
        }}
      >
        Sort by
      </Typography>

      <Box
        sx={{ display: "grid", gridTemplateColumns: "1fr 34px 75px", gap: 1 }}
      >
        <Select
          value={activeSort}
          onChange={handleSortChange}
          size="small"
          sx={{
            height: 34,
            borderRadius: 1,
            fontSize: 12,
            color: theme.palette.grey[1000],
            backgroundColor: theme.palette.common.white,
          }}
        >
          {SELLER_SORT_OPTIONS.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              <Typography sx={{ fontSize: 14 }}>{option.label}</Typography>
            </MenuItem>
          ))}
        </Select>

        <IconButton
          onClick={() => onSortChange?.(activeSort)}
          sx={{
            width: 34,
            height: 34,
            border: "1px solid",
            borderColor: theme.palette.divider,
            borderRadius: 1,
            color: theme.palette.primary.main,
          }}
        >
          {sortConfig?.sortDir === "DESC" ? (
            <ArrowUpward sx={{ fontSize: 14 }} />
          ) : (
            <ArrowDownward sx={{ fontSize: 14 }} />
          )}
        </IconButton>

        <ButtonV1
          title="Filter"
          variant="outlined"
          size="small"
          startIcon={<FilterList sx={{ fontSize: 12 }} />}
          onClick={handleOpenFilter}
          style={{
            px: 1,
            py: 0,
            borderRadius: 1,
            height: 34,
            width: 75,
            borderColor: theme.palette.divider,
            color: theme.palette.grey[1000],
          }}
          textStyle={{
            fontSize: 14,
          }}
        />
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          mt: 2,
          mb: 1,
          py: 0.75,
          borderTop: "1px solid",
          borderBottom: "1px solid",
          borderColor: theme.palette.divider,
          backgroundColor: theme.palette.grey[100],
        }}
      >
        <Checkbox
          checked={allSelected}
          indeterminate={selectedCount > 0 && !allSelected}
          onChange={handleToggleAll}
          size="small"
        />
        <Typography sx={{ color: theme.palette.grey["A1000"], fontSize: 14 }}>
          Select all sellers
        </Typography>
        <Box sx={{ flex: 1 }} />
        {selectedCount > 0 && (
          <Typography
            sx={{
              color: theme.palette.primary.main,
              fontWeight: 700,
              fontSize: 14,
            }}
          >
            {selectedCount} selected
          </Typography>
        )}
      </Box>

      <Box
        sx={{
          flex: 1,
          overflowY: !loading && sellerCards.length === 0 ? "hidden" : "auto",
          pr: 0.5,
          mr: 0,
          mt: 2,
        }}
      >
        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              marginBottom: 20,
            }}
          >
            <Loader />
          </Box>
        ) : sellerCards.length > 0 ? (
          sellerCards
        ) : (
          <Box
            sx={{
              minHeight: 200,
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Typography color="text.secondary">
              No sellers match the selected filters
            </Typography>
          </Box>
        )}
        <TablePagination
          component="div"
          count={totalCount}
          page={currentPage}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[10]}
          onPageChange={onPageChange}
          onRowsPerPageChange={onRowsPerPageChange}
          ActionsComponent={PaginationAction}
          sx={{
            flexShrink: 0,
            borderTop: "1px solid",
            borderColor: theme.palette.divider,
            "& .MuiTablePagination-toolbar": {
              px: 0,
              justifyContent: "flex-end",
            },
          }}
        />
      </Box>

      {openFilter && (
        <Suspense fallback={null}>
          <FilterModal
            key={filterResetKey}
            open={openFilter}
            onClose={handleCloseFilter}
            onSubmit={handleApplyFilters}
            onReset={handleResetFilters}
            sellerData={sellerFilterData || sellerData}
            customersData={customersData}
            fetchCustomersData={fetchCustomersData}
            buyerLoading={buyerLoading}
            appliedFilters={appliedFilters}
          />
        </Suspense>
      )}
    </Box>
  );
};

const sellerType = PropTypes.shape({
  vmId: PropTypes.string,
  VmId: PropTypes.string,
  companyName: PropTypes.string,
  state: PropTypes.string,
});

SellerList.propTypes = {
  sellerData: PropTypes.arrayOf(sellerType),
  selectedVmIds: PropTypes.arrayOf(PropTypes.string),
  onToggleSeller: PropTypes.func.isRequired,
  onToggleAllSellers: PropTypes.func.isRequired,
  onClearSelection: PropTypes.func,
  onSortChange: PropTypes.func,
  onApplyFilters: PropTypes.func.isRequired,
  sellerFilterData: PropTypes.arrayOf(sellerType),
  customersData: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  ),
  fetchCustomersData: PropTypes.func,
  buyerLoading: PropTypes.bool,
  sortConfig: PropTypes.shape({
    sortBy: PropTypes.string,
    sortDir: PropTypes.string,
  }),
  sellerPagination: PropTypes.shape({
    page: PropTypes.number,
    currentPage: PropTypes.number,
    pageSize: PropTypes.number,
    totalItems: PropTypes.number,
  }),
  onPageChange: PropTypes.func.isRequired,
  onRowsPerPageChange: PropTypes.func.isRequired,
  filterResetKey: PropTypes.number,
  loading: PropTypes.bool,
};

export default React.memo(SellerList);
